// The "Live now" strip on the overview and match list: the running match, or what the runner is doing.

import type { StatusResponse } from '../src/api/contract';
import { api } from './api';
import { everyWhileVisible, h, link } from './dom';
import { fmtCountdown, parseDate, phaseText } from './format';

function render(el: HTMLElement, st: StatusResponse): void {
  const runner = st.runner;
  const cur = st.current;
  if (cur && cur.status === 'running') {
    const turn = runner?.turn ?? cur.totalTurns;
    el.replaceChildren(
      h(
        'a',
        { class: 'banner banner--live', attrs: { href: '/live' } },
        h('span', { class: 'live-dot', attrs: { 'aria-hidden': 'true' } }),
        h('span', { class: 'banner-kicker' }, 'Live now'),
        h(
          'span',
          { class: 'banner-main' },
          h('span', { class: 'atk' }, cur.modelAName || cur.modelA),
          ' is attacking ',
          h('span', { class: 'def' }, cur.modelBName || cur.modelB),
          typeof turn === 'number' && turn > 0 ? ` · turn ${turn}` : '',
        ),
        h('span', { class: 'banner-cta' }, 'Watch →'),
      ),
    );
    el.hidden = false;
    return;
  }
  if (!runner) {
    el.hidden = true;
    return;
  }
  const bits: (Node | string)[] = [h('span', { class: 'banner-kicker' }, `No match running · ${phaseText(runner.phase)}`)];
  if (runner.detail) bits.push(h('span', { class: 'banner-main' }, runner.detail));
  else if (runner.phase === 'idle') bits.push(h('span', { class: 'banner-main' }, 'Between matches. The next one starts shortly.'));
  else bits.push(h('span', { class: 'banner-main' }));
  const next = parseDate(runner.nextActionAt);
  if (next && next.getTime() > Date.now() + 1000 && runner.phase !== 'running') {
    bits.push(h('span', { class: 'muted' }, `next step ${fmtCountdown(runner.nextActionAt)}`));
  }
  bits.push(link('/live', 'Live page →', 'banner-cta'));
  el.replaceChildren(h('div', { class: `banner banner--${runner.phase === 'error' ? 'error' : 'idle'}` }, ...bits));
  el.hidden = false;
}

/** Keeps `el` showing the arena's state, refreshed every minute while the page is visible. */
export async function liveBanner(el: HTMLElement): Promise<StatusResponse | null> {
  const refresh = async (): Promise<StatusResponse | null> => {
    try {
      const st = await api.status();
      render(el, st);
      return st;
    } catch {
      el.hidden = true;
      return null;
    }
  };
  everyWhileVisible(60_000, () => void refresh());
  return refresh();
}
